import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Popconfirm,
  Radio,
  Row,
  Select,
  Switch,
  Upload,
  Table,
} from "antd";
import dayjs from "dayjs";
import { parseDate, formatDate, DISPLAY_FORMAT } from "../../helpers/dateHelper";
import APIClient from "../../helpers/ApiClient";
import excel from "../assets/vector-icons/excel.png";
import exchange from "../assets/vector-icons/exchange.png";
import search from "../assets/vector-icons/search.png";
import UpdatedComponent from "../ExportExcel";
import CountryList from "../../common/CountryList";
import { useAuthContext } from "../../common/providers/AuthProvider";

import {
  CloseOutlined,
  StarOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  LeftSquareOutlined,
  PlusCircleFilled,
  PlusOutlined,
  StopOutlined,
  InfoCircleTwoTone,
} from "@ant-design/icons";
import HelpInfoHelper from "../../common/HelpInfoHelper/HelpInfoHelper";
import VerifyPasswordModal from "../../common/VerifyPasswordModal/VerifyPasswordModal";
import ApiClient from "../../helpers/ApiClient";

const BASE = process.env.REACT_APP_BASE_URL;
const uploadActionUrl = BASE + "media/upload/multipleImages";

const normalizeDialingCode = (code) => {
  const raw = String(code ?? "").trim();
  if (!raw) return "+91";
  return raw.startsWith("+") ? raw : `+${raw}`;
};

const resolveFormDialingCode = (values) =>
  normalizeDialingCode(values?.DialingCode);

const ImageUpload = ({
  category,
  fileList,
  setFileList,
  formName,
  limit,
  limitSize,
  rule,
  label,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const uploadLogoButton = !isUploading ? (
    <div>
      <PlusOutlined />
      <div className="ant-upload-text">Upload </div>
    </div>
  ) : (
    <div className="ant-upload-text">Loading... </div>
  );

  const toMediaUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const clean = String(path).replace(/^\.\//, "").replace(/^\//, "");
    return `${BASE}${clean}`;
  };

  function beforeUpload(file) {
    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "image/webp",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExt = /\.(jpg|jpeg|png|gif|webp|pdf|doc|docx)$/i;
    const isAllowedType =
      allowedTypes.includes(file.type) || allowedExt.test(file.name || "");

    if (!isAllowedType) {
      message.error(
        "Only JPG, JPEG, PNG, GIF, WEBP, PDF, DOC or DOCX files are allowed!"
      );
      return Upload.LIST_IGNORE || false;
    }

    const maxSize = 2 * 1024 * 1024; // 2 MB
    if (file.size > maxSize) {
      message.error("Document must be 2 MB or smaller!");
      return Upload.LIST_IGNORE || false;
    }

    return true;
  }

  function getImages(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  function handleChange(info) {
    const nextList = (info.fileList || []).map((file) => {
      const path = file.response?.data?.filepaths?.[0];
      if (file.status === "done" && path) {
        const url = toMediaUrl(path);
        return {
          ...file,
          url,
          thumbUrl: /\.(pdf|doc|docx)$/i.test(path) ? undefined : url,
        };
      }
      return file;
    });

    setFileList(nextList);
    if (info.file.status === "uploading") {
      setIsUploading(true);
      return;
    }
    if (info.file.status === "removed") {
      setIsUploading(false);
    }
    if (info.file.status === "error") {
      setIsUploading(false);
      message.error(
        info.file.error?.message ||
          "Document upload failed. Please try JPG, PNG or PDF up to 2 MB."
      );
      return;
    }
    if (info.file.status === "done") {
      setIsUploading(false);
      const resp = info.file.response;
      if (resp && resp.status && resp.status !== 200) {
        message.error(resp.message || "Document upload failed");
      } else if (resp?.status === 200) {
        message.success("Document uploaded successfully");
      }
    }
  }

  const handlePreview = async (file) => {
    const path = file.response?.data?.filepaths?.[0];
    const url = file.url || (path ? toMediaUrl(path) : "");
    if (url && /\.(pdf|doc|docx)$/i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!file.url && !file.preview && file.originFileObj) {
      file.preview = await getImages(file.originFileObj);
    }
    setPreviewVisible(true);
    setPreviewImage(file.url || file.preview || url);
    setPreviewTitle(file.name);
  };

  const handleCancel = () => {
    setPreviewVisible(false);
  };

  return (
    <>
      <Form.Item
        name={formName}
        rules={[
          {
            required: fileList.length > 0 ? false : !!rule,
            message: "Required",
          },
        ]}
        label={label}
      >
        <Upload
          name="image"
          listType="picture-card"
          className="avatar-uploader"
          showUploadList={{
            showPreviewIcon: true,
            showRemoveIcon: true,
          }}
          fileList={fileList}
          action={uploadActionUrl}
          onChange={handleChange}
          onPreview={handlePreview}
          data={{ category: category }}
          beforeUpload={beforeUpload}
        >
          {limit
            ? fileList
              ? fileList.length >= limitSize
                ? null
                : uploadLogoButton
              : uploadLogoButton
            : uploadLogoButton}
        </Upload>
      </Form.Item>
      <Modal
        destroyOnClose={true}
        open={previewVisible}
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img src={previewImage} alt="imagePreview" style={{ width: "100%" }} />
      </Modal>
    </>
  );
};
const REPRESENTATIVE_NONE_VALUE = "none";

const resolveRepresentativeEmpId = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === REPRESENTATIVE_NONE_VALUE ||
    value === 0 ||
    value === "0"
  ) {
    return null;
  }
  return value;
};

const AgentRegistration = (props) => {
  const handleVerifyPassword = (values) => {
    APIClient.post("admin/verifyTransactionPassword", {
      UserID: user.UserID,
      Password: values,
    })
      .then((res) => {
        if (res?.status === 200) {
          setVerifyModalVisible(false);
          setIsLoading(false);
          getAgentList();
          message.success(res.message);
          if (formType == 1) {
            form.submit();
          } else if (formType == 2) {
            deleteFlightComm(UserId);
          } else if (formType == 3) {
            changeStatus(userData, 0);
          } else if (formType == 4) {
            changeStatus(userData, 1);
          }
        } else {
          if (res?.message) message.error(res.message, 3);
          else message.error("Booking Failed", 3);
        }
      })
      .catch();
  };
  const [pan, setPan] = useState([]);
  const [adhar, setAdhar] = useState([]);
  const [gst, setGst] = useState([]);

  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [formType, setFormType] = useState(-1);
  const { user } = useAuthContext();

  const [otheruploads, setOtheruploads] = useState([]);
  const { Option } = Select;
  const [id, setId] = useState(-1);
  const [addMarkupModal, setAddMarkupModal] = useState(false);
  const [providersList, setProvidersList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [UserId, setUserId] = useState(-1);
  const [flightCommLists, setFlightCommLists] = useState([]);
  const [userData, setUserData] = useState({});
  const [isloading, setIsLoading] = useState(false);
  const [represntedList, setrepresntedList] = useState([])

  const disabledOriginDate = (currentDate) => {
    // Prevent crash if currentDate is missing; only allow past/today
    if (!currentDate) return false;
    return currentDate.isAfter(dayjs().endOf("day"));
  };
  function onChangeDate(date, dateString) {}
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [member, setMember] = useState([]);
  const [form] = Form.useForm();
  const validateMessages = {
    required: "",
  };
  useEffect(() => {
    getAgentList();
    getMembership();
    getAllRepresented();
  }, []);

  const getUploadedPath = (list) => {
    if (!list?.length) return "";
    const file = list[0];
    return (
      file?.response?.data?.filepaths?.[0] ||
      (file?.url
        ? String(file.url).replace(BASE, "").replace(/^\//, "")
        : "") ||
      ""
    );
  };

  const buildMediaUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const clean = String(path).replace(/^\.\//, "").replace(/^\//, "");
    return `${BASE}${clean}`;
  };

  const toSavedFileItem = (uid, name, path) => ({
    uid: String(uid),
    name: name || "document",
    status: "done",
    url: buildMediaUrl(path),
    thumbUrl: /\.(pdf|doc|docx)$/i.test(path) ? undefined : buildMediaUrl(path),
    response: {
      status: 200,
      data: { filepaths: [path] },
    },
  });
  const getMembership = () => {
    APIClient.get("admin/memberships")
      .then((res) => {
        if (res.status == 200) {
          let filterProvider = res.data.filter(
            (item) => item.ServiceType === 1
          );
          setMember(res.data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const [imagePath, setImagePath] = useState({
    company: [],
    progressbarBanners: [],
  });
  const [dataFileList, setDataFileList] = useState([]);
  const prop = {
    action: uploadActionUrl,
    listType: "picture",
    name: "image",
    fileList: [...dataFileList],
    className: "upload-list-inline",
    data: { category: "progressbarBanner" },
  };
  const handleImageChange = (fileData) => {
    if (fileData.file.status === "uploading") {
      return;
    }
    setDataFileList(fileData.fileList);
    if (fileData.file.status === "done") {
      let copy = imagePath;
      copy["progressbarBanners"].push(fileData.file.response.data.filepaths[0]);
      setImagePath(copy);
    }
  };
  const getAgentList = () => {
    APIClient.get("admin/user")
      .then((res) => {
        if (res.status == 200) {
          let filterProvider = res.data.filter(
            (item) => item?.role?.Level === 3
          );

          let data = filterProvider.map((item, index) => {
            let FirstName = item.userDetails ? item.userDetails.FirstName : "";
            let LastName = item.userDetails ? item.userDetails.LastName : "";
            let service =
              item.Services != null
                ? Array.from(String(item.Services).split("$"), Number).filter(
                    (n) => Number.isFinite(n) && n > 0,
                  )
                : [];
            const holdFlags =
              item.Hold != null && item.Hold !== ""
                ? Array.from(String(item.Hold).split("$"), Number).filter(
                    (n) => Number.isFinite(n) && n > 0,
                  )
                : [];
            const depositFlags =
              item.Deposit != null && item.Deposit !== ""
                ? Array.from(String(item.Deposit).split("$"), Number).filter(
                    (n) => Number.isFinite(n) && n > 0,
                  )
                : [];

            return {
              ...item,
              SNo: index + 1,

              UserID: item.UserID,
              Wallet: item.agentWalletDetails
                ? item.agentWalletDetails.Amount != undefined
                  ? item.agentWalletDetails.Amount
                  : 0
                : 0,
              Amount: item.Amount != undefined ? item.Amount : "",
              Credit: item.agentWalletDetails
                ? item.agentWalletDetails.CreditAmount != undefined
                  ? item.agentWalletDetails.CreditAmount
                  : 0
                : 0,
              CreditDate: item.agentWalletDetails
                ? item.userDetails.DOB != undefined
                  ? formatDate(item.agentWalletDetails.FromDate, DISPLAY_FORMAT)
                  : ""
                : "",
              ExpiryDate: item.agentWalletDetails
                ? item.agentWalletDetails.ExpiryDate != undefined && item.agentWalletDetails.ExpiryDate != ""
                  ? formatDate(item.agentWalletDetails.ExpiryDate, DISPLAY_FORMAT)
                  : ""
                : "",
              Title: item.userDetails ? item.userDetails.Title : "",
              FirstName: item.userDetails ? item.userDetails.FirstName : "",
              LastName: item.userDetails ? item.userDetails.LastName : "",
              AgentName: FirstName + " " + LastName,
              DOB: item.userDetails?.DOB
                ? parseDate(item.userDetails.DOB)
                : null,
              Services: service,
              Hold: holdFlags,
              Deposit: depositFlags,
              MembershipType: item.Membership,
              Gender: item.userDetails ? item.userDetails.Gender : "",
              Email: item.Email,
              PANUpload: item.userDetails ? item.userDetails.PANUpload : "",
              GSTUpload: item.userDetails ? item.userDetails.GSTUpload : "",
              AdhaarCardUpload: item.userDetails
                ? item.userDetails.AdhaarCardUpload
                : "",
              OtherDocumentsUpload: item.userDetails
                ? item.userDetails.OtherDocumentsUpload
                : "",
              MobileNumber: item.Mobile,
              DialingCode: normalizeDialingCode(
                item.DailingCode ?? item.DialingCode,
              ),
              AgencyName: item.userDetails ? item.userDetails.AgencyName : "",
              StoreAddress: item.userDetails ? item.userDetails.Address1 : "",
              RegisteredAddress: item.userDetails
                ? item.userDetails.Address2
                : "",
              Comments: item.userDetails ? item.userDetails.Comments : "",
              Status: item.Status,
              AdhaarNumber: item.userDetails ? item.userDetails.AadharCard : "",
              PANNumber: item.userDetails ? item.userDetails.PAN : "",
              GSTINNumber: item?.GSTDetails ? item?.GSTDetails.GSTINNumber : "",
              GSTCompanyName: item?.userGstvat?.GstCompanyName ?? "",
              GstNumber: item?.userGstvat?.GstNumber ?? "",
              GstCompanyContactNumber:
                item?.userGstvat?.GstCompanyContactNumber ?? "",
              GstCompanyEmail: item?.userGstvat?.GstCompanyEmail ?? "",
              GstCompanyAddress: item?.userGstvat?.GstCompanyAddress ?? "",
            };
          });

          setFlightCommLists(data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const [currentuser, setCurrentUser] = useState({});
  const [UploadGSTprops, setUploadGSTprops] = useState({});
  const [UploadPANprops, setUploadPANprops] = useState({});
  const [UploadADHARprops, setUploadADHARprops] = useState({});
  const updateFlightComm = (currentData) => {
    setUploadGSTprops({
      showUploadList: {
        showDownloadIcon: true,
        downloadIcon: "download ",
        showRemoveIcon: true,
        removeIcon: <StarOutlined />,
      },
    });

    setUploadADHARprops({
      showUploadList: {
        showDownloadIcon: true,
        downloadIcon: "download ",
        showRemoveIcon: true,
        removeIcon: <StarOutlined />,
      },
    });
    setUploadPANprops({
      showUploadList: {
        showDownloadIcon: true,
        downloadIcon: "download ",
        showRemoveIcon: true,
        removeIcon: <StarOutlined />,
      },
    });
    const toServiceFlagArray = (raw) => {
      if (Array.isArray(raw)) {
        return raw
          .map((e) => parseInt(e, 10))
          .filter((n) => Number.isFinite(n) && n > 0);
      }
      if (raw == null || raw === "") {
        return [];
      }
      return String(raw)
        .split("$")
        .map((e) => parseInt(String(e).trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
    };

    currentData.Services = toServiceFlagArray(currentData.Services);
    currentData.Hold = toServiceFlagArray(currentData.Hold);
    currentData.Deposit = toServiceFlagArray(currentData.Deposit);

    setIsEditMode(true);
    setUserId(currentData.UserID);
    setCurrentUser(currentData);
    console.log(currentData, "currentData");
    form.setFieldsValue({
      ...currentData,
      DialingCode: normalizeDialingCode(
        currentData.DialingCode ?? currentData.DailingCode,
      ),
      represnted: currentData.empId ? currentData.empId : REPRESENTATIVE_NONE_VALUE,
      GSTUpload: currentData.GSTAdress,
      PANUpload: currentData.PANUpload,
      DOB: currentData.DOB ? parseDate(currentData.DOB) : null,
      ExpiryDate: currentData.ExpiryDate ? parseDate(currentData.ExpiryDate) : "",
      Gender: currentData.Gender,
      servicetype: currentData.Services,
      Services: currentData.Services,
      Hold: currentData.Hold,
      Deposit: currentData.Deposit,
      AdhaarCardUpload: currentData.AdhaarCardUpload,
      GSTINNumber: currentData?.userGstVat?.GstNumber,
    });

    if (currentData.GSTUpload) {
      setGst([
        toSavedFileItem(
          `${currentData.UserID}-gst`,
          "GST",
          currentData.GSTUpload
        ),
      ]);
    } else {
      setGst([]);
    }
    if (currentData.AdhaarCardUpload) {
      setAdhar([
        toSavedFileItem(
          `${currentData.UserID}-aadhaar`,
          "Aadhaar",
          currentData.AdhaarCardUpload
        ),
      ]);
    } else {
      setAdhar([]);
    }

    if (currentData.PANUpload) {
      setPan([
        toSavedFileItem(
          `${currentData.UserID}-pan`,
          "PAN",
          currentData.PANUpload
        ),
      ]);
    } else {
      setPan([]);
    }

    if (currentData.OtherDocumentsUpload) {
      const otherPaths = String(currentData.OtherDocumentsUpload)
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
      setOtheruploads(
        otherPaths.map((path, idx) =>
          toSavedFileItem(`${currentData.UserID}-other-${idx}`, "Other", path)
        )
      );
    } else {
      setOtheruploads([]);
    }

    setAddMarkupModal(true);
  };
  const prefixSelector = (
    <Form.Item name="DialingCode" noStyle>
      <Select style={{ width: 80 }} showSearch>
        {CountryList.map((item) => (
          <Option key={item.dial_code} value={item.dial_code}>
            {item.dial_code}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  const deleteFlightComm = (UserID) => {
    APIClient.delete("admin/user/" + UserID)
      .then((res) => {
        if (res.status == 200) {
          message.success("Deleted  successfully", 3);
          getAgentList();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const searchData = () => {
    setShowSearchBox(true);
  };
  const closeSearch = () => {
    setIsLoading(false);
    getAgentList();
    setShowSearchBox(false);
  };

  const closeSearchInput = (
    <div>
      <CloseOutlined onClick={closeSearch} />
    </div>
  );
  const revokeCredit = (Credit, userID) => {
    const data = {
      CreditAmount: Credit,
      AddrRevoke: 3,
      UserID: userID,
      DeviceOS: 0,
      DeviceOSVersion: "string",
      ApplicationType: 0,
      DeviceToken: "string",
      FirBaseToken: "string",
      CreatedBy: 0,
    };
    APIClient.post("admin/AddrRevoke/DepositRequest", data)
      .then((response) => {
        if (response.status == 200) {
          if (response.data.AddrRevoke == 1) {
            message.success("Amount Added SuccessFully", 3);
          } else {
            message.success("Amount Revoked Successfully", 3);
          }
          getAgentList();
        } else if (response.status == 400) {
          message.error("Enter Required Fields", 3);
        } else if (response.status == 404) {
          message.error("Not Found", 3);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const tableActions = (currentFlightComm) => {
    return (
      <div style={{ display: "flex", justifyContent: "space-evenly" }}>
        <div className="edit-icon" style={{ textAlign: "center" }}>
          <EditOutlined
            onClick={() => updateFlightComm(currentFlightComm)}
            style={{ color: "#1890ff", fontSize: "20px", textAlign: "center" }}
          />
        </div>
        <div className="edit-icon" style={{ textAlign: "center" }}>
          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() => {
              setFormType(2);
              setVerifyModalVisible(true);
              setUserId(currentFlightComm.UserID);
            }}
          >
            <DeleteOutlined
              style={{ color: "red", fontSize: "20px", textAlign: "center" }}
            />
          </Popconfirm>
        </div>
        {currentFlightComm.Credit > 0 ? (
          <div className="edit-icon" style={{ textAlign: "center" }}>
            <Popconfirm
              title="Are you sure to revoke Credit?"
              onConfirm={() =>
                revokeCredit(currentFlightComm.Credit, currentFlightComm.UserID)
              }
            >
              <LeftSquareOutlined
                style={{ color: "black", fontSize: "20px" }}
              />
            </Popconfirm>
          </div>
        ) : (
          <div className="edit-icon" style={{ textAlign: "center" }}>
            <Popover content="No Credit">
              <StopOutlined
                style={{ color: "red", fontSize: "20px", textAlign: "center" }}
              />
            </Popover>
          </div>
        )}
      </div>
    );
  };

  const add = () => {
    setGst([]);
    setPan([]);
    setAdhar([]);
    setOtheruploads([]);
    form.resetFields();
    if (isEditMode) {
      setIsEditMode(false);
      form.resetFields();
    }
    // Clear browser autofill on Email / Password
    form.setFieldsValue({ Email: "", Password: "", DOB: null });
    setTimeout(() => {
      form.setFieldsValue({ Email: "", Password: "" });
    }, 100);
    setAddMarkupModal(true);
  };

  const clear = () => {
    setAddMarkupModal(false);
  };

  const tableColumns = [
    {
      title: "S.No",
      dataIndex: "SNo",
    },

    {
      title: "Membership",
      dataIndex: "memberShip",
      render: (memberShip) => (memberShip ? memberShip?.Name : ""),
    },

    {
      title: "Agency Name",
      dataIndex: "AgencyName",
      sorter: (a, b) => a.AgencyName?.localeCompare(b.AgencyName),
    },
    {
      title: "Partner ID",
      dataIndex: "AgentID",
    },
    {
      title: "Partner Name",
      dataIndex: "AgentName",
      sorter: (a, b) => a.AgentName?.localeCompare(b.AgentName),
    },
    {
      title: "Email",
      dataIndex: "Email",
      sorter: (a, b) => a.Email?.localeCompare(b.Email),
    },
    {
      title: "Mobile",
      dataIndex: "Mobile",
      sorter: (a, b) => a.Mobile?.localeCompare(b.Mobile),
    },
    {
      title: "Wallet",
      dataIndex: "Wallet",
      sorter: (a, b) => a.Wallet - b.Wallet,
    },
    {
      title: "Credit",
      dataIndex: "Credit",

      sorter: (a, b) => a.Credit - b.Credit,
    },
    // {
    //   title: "CreditDate",
    //   dataIndex: "CreditDate",
    //   //render: (CreditDate) =>CreditDate?formatDate(CreditDate, "DD-MM-YYYY"):"",
    //   sorter: (a, b) => a.CreditDate?.localeCompare(b.CreditDate),
    //  //render: (date) => date.CreditDate?formatDate(date.CreditDate, "DD-MM-YYYY"):"",

    // },
    {
      title: "ExpiryDate",
      dataIndex: "ExpiryDate",
      //  render: (ExpiryDate) =>ExpiryDate?formatDate(ExpiryDate, "DD-MM-YYYY"):"",
      sorter: (a, b) => a.ExpiryDate?.localeCompare(b.ExpiryDate),
    },

    {
      title: "Actions",
      render: (currentItem) => tableActions(currentItem),
    },
    {
      title: "Status",

      render: (currentItem) => handelStatus(currentItem),
    },
  ];
  const handelStatus = (data) => {
    // if (data.Status == 1) {
      return (
        <Switch
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          // defaultChecked
          checked={data.Status == 1 ? true: false}
          onChange={() => {
            setVerifyModalVisible(true);
            setFormType(data.Status == 1 ? 3 : 4);
            setUserData(data);
          }}
        />
      );
    // } else {
    //   return (
    //     <Switch
    //       checkedChildren="Active"
    //       unCheckedChildren="Inactive"
    //       onChange={() => {
    //         setVerifyModalVisible(true);
    //         setFormType(4);
    //         setUserData(data);
    //       }}
    //     />
    //   );
    // }
  };
  const changeStatus = (values, Status) => {
    const id = values.UserID;
    //delete info.SNo
    //delete info.categoryId
    values.Services = values?.Services?.split("$");
    setUserId(values.UserID);
    const dialCode = resolveFormDialingCode(values);
    let obj = {
      FirstName: values.FirstName,
      LastName: values.LastName,
      Email: values.Email,
      DialingCode: dialCode,
      DailingCode: dialCode,
      Mobile: values.MobileNumber,
      DOB: values.DOB,
      Gender: values.Gender,
      StoreAddress: values.StoreAddress,
      Status: Status,
      RegisterdAddress: values.RegisteredAddress,
      AgencyLogo: currentuser.AgencyLogo,
      AgencyName: values.AgencyName,
      Membership: values.MembershipType,
      Services: values.Services,
      Hold: values.Hold,
      Deposit: values.Deposit,
      PaymentPreference: [1],
      Role: 5,
      PAN: values.PANNumber,
      PANUpload: values.PANUpload ? values.PANUpload : "",
      AdhaarCard: values.AdhaarNumber,
      // AdhaarCardUpload: adharPath ? `${BASE}${adharPath.substring(1)}` : "",
      AdhaarCardUpload: values.AdhaarCardUpload ? values.AdhaarCardUpload : "",
      // GSTUpload: GSTUpload ? `${BASE}${GSTUpload.substring(1)}` : "",
      GSTUpload: values.GSTUpload ? values.GSTUpload : "",
      LicenseNumber: "string",
      VoterId: "string",
      Passport: "string",
      PassportUpload: "string",
      OtherDocumentsUpload: values.OtherDocumentsUpload
        ? values.OtherDocumentsUpload
        : "",
      Comments: values.Comments,
      Title: values.Title,
      GSTDetails: {
        GSTCompanyName: values?.userGstVat?.GstCompanyName ?? "",
        GSTINNumber: values?.userGstVat?.GstNumber ?? "",
        GSTAdress: values?.userGstVat?.GstCompanyAddress ?? "",
        GSTPhone: values?.userGstVat?.GstCompanyContactNumber ?? "",
        GSTEmail: values?.userGstVat?.GstCompanyEmail ?? "",
      },
      ModifiedBy: 0,
      ModifiedDate: new Date(),
    };
    submitUpdateMap(obj, id);
  };

  const submitForm = (values) => {
    let adharPath = getUploadedPath(adhar);
    let panUpload = getUploadedPath(pan);
    let GSTUpload = getUploadedPath(gst);
    let otherUpload = getUploadedPath(otheruploads);

    if (values.Gender == "Male") {
      values.Gender = 1;
    } else if (values.Gender == "Female" || values.Gender == "FeMale") {
      values.Gender = 2;
    } else if (values.Gender == "Other") {
      values.Gender = 3;
    } else {
      values.Gender = values.Gender || 0;
    }

    // Safe DOB — never crash on invalid/empty date
    let dobValue = "";
    try {
      if (values?.DOB) {
        dobValue = formatDate(values.DOB, "YYYY-MM-DD") || "";
      }
    } catch (e) {
      dobValue = "";
    }

    const dialCode = resolveFormDialingCode(values);

    let data = {
      empId: resolveRepresentativeEmpId(values.represnted),
      Title: values.Title,
      FirstName: values.FirstName,
      LastName: values.LastName,
      Email: values.Email,
      DialingCode: dialCode,
      DailingCode: dialCode,
      Mobile: values.MobileNumber,
      AgencyName: values.AgencyName,
      Password: values.Password,
      DOB: dobValue,
      Gender: values.Gender,
      StoreAddress: values.StoreAddress,
      RegisterdAddress: values.RegisteredAddress,
      Comments: values.Comments,
      PAN: values.PANNumber,
      PANUpload: panUpload ? panUpload : "",
      AdhaarCard: values.AdhaarNumber,
      AdhaarCardUpload: adharPath ? adharPath : "",
      GSTUpload: GSTUpload ? GSTUpload : "",
      LicenseNumber: values.LicenseNumber ? values.LicenseNumber : " ",
      VoterId: values.VoterId ? values.VoterId : "",
      Passport: values.Passport ? values.Passport : "",
      PassportUpload: values.PassportUpload ? values.PassportUpload : "",
      OtherDocumentsUpload: otherUpload ? otherUpload : "",
      Role: 7,
      Status: 1,
      Membership: values.MembershipType,
      Services: values.Services ?? values.servicetype,
      Hold: values.Hold,
      Deposit: values.Deposit,
      DeviceOS: 1,
      ApplicationType: 1,
      GSTDetails: {
        GSTCompanyName: "",
        GSTINNumber: values.GSTINNumber || "",
        GSTAdress: "",
        GSTPhone: "",
        GSTEmail: "",
      },
      CreadtedBy: 0,
      //CreatedDate: 0,
      DeviceToken: "string",
      DeviceType: "string",
      FirBaseToken: "string",
      DeviceOSVersion: "string",
    };

    let regObj = {
      empId: resolveRepresentativeEmpId(values.represnted),
      FirstName: values.FirstName,
      LastName: values.LastName,
      Email: values.Email,
      DialingCode: dialCode,
      DailingCode: dialCode,
      Mobile: values.MobileNumber,
      DOB: dobValue,
      Gender: values.Gender,
      Status: 1,
      StoreAddress: values.StoreAddress,
      RegisterdAddress: values.RegisteredAddress,
      AgencyLogo: currentuser?.AgencyLogo ?? "",
      AgencyName: values.AgencyName,
      Membership: values.MembershipType,
      Services: values.Services ?? values.servicetype,
      Hold: values.Hold,
      Deposit: values.Deposit,
      PaymentPreference: [1],
      Role: 5,
      PAN: values.PANNumber,
      PANUpload: panUpload ?? "",
      AdhaarCard: values.AdhaarNumber,
      //AdhaarCardUpload: "kkkkkk",
      AdhaarCardUpload: adharPath ?? "",
      GSTUpload: GSTUpload ?? "",
      LicenseNumber: "",
      VoterId: "",
      Passport: "",
      PassportUpload: "",
      OtherDocumentsUpload: otherUpload ?? "",
      Comments: values.Comments ? values.Comments : "",
      Title: values.Title,
      GSTDetails: {
        GSTCompanyName: currentuser?.userGstVat?.GstCompanyName ?? "",
        GSTINNumber: values.GSTINNumber ?? "",
        GSTAdress: currentuser?.userGstVat?.GstCompanyAddress ?? "",
        GSTPhone: currentuser?.userGstVat?.GstCompanyContactNumber ?? "",
        GSTEmail: currentuser?.userGstVat?.GstCompanyEmail ?? "",
      },
      ModifiedBy: 0,
      ModifiedDate: new Date(),
    };

    isEditMode ? submitUpdateMap(regObj, UserId) : submitMapDetails(data);
  };

  const submitMapDetails = (formMapData) => {
    APIClient.post("admin/b2b/signUp", formMapData)
      .then((response) => {
        if (response.status == 200) {
          message.success("Partner created SuccessFully", 3);

          setAddMarkupModal(false);
          getAgentList();
          form.resetFields();
        } else if (response.status == 201) {
          message.error("Inserted Successfully", 3);
          setAddMarkupModal(false);
        } else if (response.status == 500) {
          message.error("Duplicate Entry For Email or Mobile", 3);
          setAddMarkupModal(false);
        } else if (response.status == 409) {
          message.error("Entered Mobile Number or Mail Already existed", 3);
        } else if (response.status == 404) {
          message.error("Not Found", 3);
        } else {
          message.error("Something Went Wrong", 3);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const submitUpdateMap = (formMapData, UserId) => {
    const normalizeFlags = (raw) => {
      if (Array.isArray(raw)) {
        return raw
          .map((x) => Number(x))
          .filter((n) => Number.isFinite(n) && n > 0);
      }
      if (raw == null || raw === "") {
        return [];
      }
      return String(raw)
        .split(/[$|,]/)
        .map((x) => Number(String(x).trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    };
    const payload = {
      ...formMapData,
      Services: normalizeFlags(formMapData?.Services ?? formMapData?.servicetype),
      Hold: normalizeFlags(formMapData?.Hold),
      Deposit: normalizeFlags(formMapData?.Deposit),
    };
    APIClient.put(`admin/b2b/user/${UserId}`, {}, payload)
      .then((response) => {
        if (response.status == 200) {
          message.success("Details Updated SuccessFully", 3);
          setAddMarkupModal(false);
          getAgentList();
          form.resetFields();
        } else if (response.status == 404) {
          message.error("Not Found", 3);
        } else if (response.status == 500) {
          message.error("Duplicate Entry For Email or Mobile", 3);
          setAddMarkupModal(false);
        } else {
          message.error("Something Went Wrong", 3);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const searchTableData = (e) => {
    var searchVal = e.target.value;
    if (searchVal.length > 0) {
      const searchResults = flightCommLists.filter(function (item) {
        if (item != undefined) {
          return JSON.stringify(item)
            .toLowerCase()
            .includes(searchVal.toLowerCase());
        }
      });

      setFlightCommLists(searchResults);
    } else {
      getAgentList();
    }
  };

  const handleExcel = () => {
    const excelData = flightCommLists.map((item) => {
      return {
        S_No: item.SNo,
        Email: item.Email,
        AgencyName: item.AgencyName,
        AgentName: item.FirstName + "" + item.LastName,
        Mobile: item.Mobile,
        Wallet: item.Wallet,
        Credit: item.Credit,
        CreditDate: item.CreditDate,
        ExpiryDate: item.ExpiryDate,
        DOB: item.DOB ? formatDate(item.DOB, DISPLAY_FORMAT) : "",
        Status: item.Status,
      };
    });

    props.exportExcel(excelData, "Registration");
  };
  let lastIndex = 0;
  const unixColumn = () => {
    lastIndex++;
    return lastIndex;
  }

  const getAllRepresented = () => {
    APIClient.get("admin/represented")
      .then((res) => {
        if (res.status == 200) {
          setrepresntedList(res.data);
        } else {
          message.error("Something went wrong", 3);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <div>
      <div className="manage-markup-section">
        <div className="container-fluid">
          <Row>
            <Col span={24}>
              <div className="card-bt-gap">
                <Card bordered={false}>
                  <div className="card-add-heading">
                    <div className="rows-count">
                      <div>
                        <h5>
                          View Partner Details{" "}
                          <HelpInfoHelper
                            screenName={"/admin/agentregistration"}
                          />
                        </h5>
                        <p>{flightCommLists.length} rows found !</p>
                      </div>
                    </div>
                    <div className="action-images">
                      {showSearchBox && (
                        <Input
                          placeholder="Search"
                          onChange={(e) => searchTableData(e)}
                          suffix={closeSearchInput}
                          style={{ padding: "0px 12px" }}
                        />
                      )}
                      &nbsp;&nbsp;
                      <img
                        src={search}
                        alt="search"
                        onClick={(e) => searchData()}
                      />
                      <img src={excel} alt="excel" onClick={handleExcel} />
                      <img
                        src={exchange}
                        alt="exchange"
                        onClick={() => getAgentList()}
                      />
                      <div onClick={add} className="add-deposit-icon">
                        <PlusCircleFilled />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Table
                      scroll={{ x: true }}
                      bordered
                      dataSource={flightCommLists}
                      columns={tableColumns}
                      rowKey={unixColumn}
                      pagination={{
                        defaultPageSize: 25,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "50", "100", "125"],
                      }}
                    />
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <Modal
        width={"1000px"}
        className="admin-bc-level"
        title={[
          <div>
            <h6 style={{ marginBottom: "0px" }}>
              <strong>Partner Registration</strong>
            </h6>
          </div>,
        ]}
        visible={addMarkupModal}
        onCancel={(e) => setAddMarkupModal(false)}
        footer={[
          <div>
            <Button key="submit" type="danger" onClick={clear}>
              Cancel
            </Button>
            <Button
              key="add"
              type="primary"
              onClick={() => {
                form
                  .validateFields()
                  .then(() => {
                    setVerifyModalVisible(true);
                    setFormType(1);
                  })
                  .catch((e) => {});
              }}
            >
              {isEditMode ? "Update" : "Add"}
            </Button>
          </div>,
        ]}
      >
        <Form
          layout={"vertical"}
          form={form}
          onFinish={submitForm}
          validateMessages={validateMessages}
          autoComplete="off"
          initialValues={{ Email: "", Password: "", DialingCode: "+91" }}
        >
          <div>
            <Row gutter={16}>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="Title"
                  name="Title"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="Title">
                    <Option value="Mr">Mr</Option>
                    <Option value="Ms">Ms</Option>
                    <Option value="Mrs">Mrs</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="FirstName"
                  name="FirstName"
                  rules={[
                    { required: true },
                    {
                      pattern: "^[a-zA-Z ]*$",

                      message: "Must be Alphabet",
                    },
                  ]}
                >
                  <Input placeholder="Enter FirstName" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="LastName"
                  name="LastName"
                  rules={[
                    { required: true },
                    {
                      pattern: "^[a-zA-Z ]*$",

                      message: "Must be Alphabet",
                    },
                  ]}
                >
                  <Input placeholder="Enter LastName" />
                </Form.Item>
              </Col>

              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item label="DOB" name="DOB">
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    disabledDate={disabledOriginDate}
                    placeholder="DOB (Optional)"
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="MembershipType"
                  name="MembershipType"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="Select members">
                    {member.map((data, index) => (
                      <Option key={index} value={data.MembershipID}>
                        {data.Name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {/* <Col className="gutter-row" md={8} xs={24}>
                <Form.Item
                  label="Membership"
                  name="MembershipID"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="Select">
                    <Option value={1}>Gold</Option>
                    <Option value={2}>Silver</Option>
                    <Option value={3}>Platinum</Option>
                    <Option value={4}>GoldPlus</Option>
                    <Option value={5}>
                      SilverPlus
                    </Option>
                    <Option value={6}>
                      PlatinumPlus
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={8} xs={24}>
                <Form.Item
                  label="Agent"
                  name="AgentID"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="Select Agent">
                    <Option value={1}>Agent1</Option>
                    <Option value={2}>Agent2</Option>
                    <Option value={3}>Agent3</Option>
                    <Option value={4}>Agent4</Option>
                  </Select>
                </Form.Item>
              </Col> */}
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="Gender"
                  name="Gender"
                  rules={[{ required: true, message: "Gender is required" }]}
                >
                  <Select placeholder="Gender">
                    <Option value={1}>Male</Option>
                    <Option value={2}>Female</Option>
                    <Option value={3}>Other</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="Email"
                  name="Email"
                  rules={[
                    {
                      type: "email",
                      required: true,
                      message: "Email is required",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Email"
                    autoComplete="off"
                    name="admin-partner-email"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="Mobile Number"
                  name="MobileNumber"
                  rules={[
                    { required: true },
                    {
                      minLength: 10,
                      maxLength: 10,
                      pattern: "^[0-9]{10}$",
                      message: "Must be 10 digits",
                    },
                  ]}
                >
                  <Input
                    addonBefore={prefixSelector}
                    placeholder="Enter Mobile Number"
                  />
                </Form.Item>
              </Col>
              {isEditMode == false ? (
                <Col className="gutter-row" md={6} xs={24}>
                  <Form.Item
                    label="Password"
                    name="Password"
                    rules={[{ required: true }]}
                  >
                    <Input.Password
                      placeholder="Enter Password"
                      size="small"
                      style={{ height: "35px" }}
                      autoComplete="new-password"
                      name="admin-partner-password"
                    />
                  </Form.Item>
                </Col>
              ) : (
                ""
              )}
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="AgencyName"
                  name="AgencyName"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="Agency Name" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="StoreAddress"
                  name="StoreAddress"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="Enter Store Address" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="Registered Address"
                  name="RegisteredAddress"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="Registered Address" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={6} xs={24}>
                <Form.Item
                  label="Re-Presnted"
                  name="represnted"
                  // rules={[{ required: true }]}
                >
                    <Select placeholder="Re-Presnted">
                      {represntedList.map((item) => (
                        <Option key={item.empId} value={item.empId}>
                          {item.name} {item.last_name}
                        </Option>
                      ))}
                      <Option key={REPRESENTATIVE_NONE_VALUE} value={REPRESENTATIVE_NONE_VALUE}>
                        None
                      </Option>
                    </Select>
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={4} xs={24}>
                <Form.Item
                  label="Comments"
                  name="Comments"
                  // rules={[{ required: true }]}
                >
                  <Input placeholder="Enter comments" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={5} xs={24}>
                <Form.Item
                  label="Status"
                  name="Status"
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Radio value={1}>Active</Radio>
                    <Radio value={0}>Inactive</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col md={15} xs={24}>
                <Form.Item
                  name="servicetype"
                  label="ServiceType"
                  rules={[{ required: true, message: "service is required" }]}
                >
                  <Checkbox.Group defaultValue={["Flights"]}>
                    <Row>
                      <Checkbox value={1}>Flights</Checkbox>
                      <Checkbox value={2}>Hotels</Checkbox>
                      <Checkbox value={3}>Bus</Checkbox>
                      <Checkbox value={4}>Activities</Checkbox>
                      <Checkbox value={5}>Packages</Checkbox>
                      <Checkbox value={6}>Sasta Europe</Checkbox>
                      <Checkbox value={10}>Fixed Departures</Checkbox>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
              <Col md={15} xs={24}>
                <Form.Item
                  name="Deposit"
                  label="IsDeposit"
                  rules={[{ required: true, message: "service is required" }]}
                >
                  <Checkbox.Group defaultValue={["Flights"]}>
                    <Row>
                      <Checkbox value={1}>Flights</Checkbox>
                      <Checkbox value={2}>Hotels</Checkbox>
                      <Checkbox value={3}>Bus</Checkbox>
                      <Checkbox value={4}>Activities</Checkbox>
                      <Checkbox value={5}>Packages</Checkbox>
                      <Checkbox value={6}>Sasta Europe</Checkbox>
                      <Checkbox value={10}>Fixed Departures</Checkbox>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
              <Col md={15} xs={24}>
                <Form.Item
                  name="Hold"
                  label="IsHold"
                  rules={[{ required: true, message: "service is required" }]}
                >
                  <Checkbox.Group defaultValue={["Flights"]}>
                    <Row>
                      <Checkbox value={1}>Flights</Checkbox>
                      <Checkbox value={2}>Hotels</Checkbox>
                      <Checkbox value={3}>Bus</Checkbox>
                      <Checkbox value={4}>Activities</Checkbox>
                      <Checkbox value={5}>Packages</Checkbox>
                      <Checkbox value={6}>Sasta Europe</Checkbox>
                      <Checkbox value={10}>Fixed Departures</Checkbox>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
              
            </Row>
            {/* <Row>
              <Col md={24} xs={24}>
                <Form.Item name="servicetype" label="ServiceType" rules={[{ required: true, message: "service is required" }]}>
                  <Checkbox.Group defaultValue={["Flights"]}>
                    <Row>
                      <Checkbox value={1}>Flights</Checkbox>
                      <Checkbox value={2}>Hotels</Checkbox>
                      <Checkbox value={3}>Bus</Checkbox>
                      <Checkbox value={4}>Activities</Checkbox>
                      <Checkbox value={5}>Build Package</Checkbox>
                      <Checkbox value={6}>Sasta Europe</Checkbox>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row> */}
            <Row>
              <div>
                <h5>ID Information</h5>
              </div>
            </Row>
            <Row>
              <Col className="gutter-row" md={8} xs={24}>
                <Form.Item
                  label="GST"
                  name="GSTINNumber"
                  rules={[
                    { required: true, message: "GST Number is required" },
                    {
                      pattern: /^[a-zA-Z0-9]{15}$/,
                      message: "Must be 15 digits AlphaNumeric Number",
                    },
                  ]}
                >
                  <Input placeholder="Enter GST" />
                </Form.Item>
              </Col>

              <Col className="gutter-row" md={8} xs={24}>
                <Form.Item
                  label="Aadhaar Number"
                  name="AdhaarNumber"
                  rules={[
                    { required: true, message: "Aadhaar Number is required" },
                    {
                      pattern: /^[0-9]{12}$/,
                      message: "Must be 12 digits",
                    },
                  ]}
                >
                  <Input placeholder="Aadhaar Number" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" md={8} xs={24}>
                <Form.Item
                  label="PAN Number"
                  name="PANNumber"
                  rules={[
                    { required: true, message: "PAN Number is required" },
                    {
                      pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                      message: "Must be valid PAN (e.g. ABCDF6789G)",
                    },
                  ]}
                >
                  <Input placeholder="ABCDF6789G" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={6} xs={12}>
                <div className="upload-logo">
                  <ImageUpload
                    category="gst"
                    fileList={gst}
                    setFileList={setGst}
                    formName="GSTUpload"
                    limit={true}
                    limitSize={1}
                    rule={true}
                    label={"GST Upload"}
                  />
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="upload-logo">
                  <ImageUpload
                    category="adhar"
                    fileList={adhar}
                    setFileList={setAdhar}
                    formName="AdhaarCardUpload"
                    limit={true}
                    limitSize={1}
                    rule={true}
                    label={"Aadhaar Upload"}
                  />
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="upload-logo">
                  <ImageUpload
                    category="pan"
                    fileList={pan}
                    setFileList={setPan}
                    formName="PANUpload"
                    limit={true}
                    limitSize={1}
                    rule={true}
                    label={"PAN Upload"}
                  />
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="upload-logo">
                  <ImageUpload
                    category="otheruploads"
                    fileList={otheruploads}
                    setFileList={setOtheruploads}
                    formName="OtherUploads"
                    limit={true}
                    limitSize={6}
                    rule={false}
                    label={"Other Uploads"}
                  />
                </div>
              </Col>
            </Row>
          </div>
          {/* <hr /> */}
          {/* <hr /> */}
        </Form>
      </Modal>

      {verifyModalVisible && (
        <VerifyPasswordModal
          visible={verifyModalVisible}
          setVerifyModalVisible={setVerifyModalVisible}
          handleVerifyPassword={handleVerifyPassword}
        />
      )}
    </div>
  );
};

export default UpdatedComponent(AgentRegistration);
